import type { OnDestroy, OnInit } from '@angular/core';
import { Component, HostListener, Input, inject } from '@angular/core';
import type { FormControl, FormGroup } from '@angular/forms';
import { NonNullableFormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import { Actions, ofType } from '@ngrx/effects';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { TranslateService, TranslateModule } from '@ngx-translate/core';

import { ESavePageErrorCodes } from '@common/enums';
import type { SitePageEntity } from '@common/types/site-page.type';

import { PagesFacade } from '@web/store/website/pages/pages.facade';

import PagesActions from '@web/store/website/pages/pages.actions';
import { PageNameValidatorDirective } from '@web/core/validators/page-name.validator';
import { NgIf } from '@angular/common';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { PageUrlValidatorDirective } from '@web/core/validators/page-url.validator';

type CopyPageModalForm = {
    name: FormControl<string>;
    url: FormControl<string>;
};

@Component({
    selector: 'web-copy-page-modal',
    templateUrl: './copy-page-modal.component.html',
    styleUrls: ['./copy-page-modal.component.less'],
    imports: [
        ReactiveFormsModule,
        PageNameValidatorDirective,
        NgIf,
        TooltipModule,
        PageUrlValidatorDirective,
        TranslateModule,
    ],
})
export class CopyPageModalComponent implements OnInit, OnDestroy {
    private readonly actions$ = inject(Actions);
    private readonly bsModalRef = inject(BsModalRef);
    private readonly fb = inject(NonNullableFormBuilder);
    private readonly pagesFacade = inject(PagesFacade);
    private readonly translate = inject(TranslateService);

    @Input() public siteId!: number;
    @Input() public page!: SitePageEntity;
    @Input() public siteDomain!: string;

    public form: FormGroup<CopyPageModalForm>;

    public readonly savePageErrorCodes = ESavePageErrorCodes;

    private readonly unsubscribe$ = new Subject<void>();

    public ngOnInit(): void {
        this.initForm();

        this.actions$.pipe(ofType(PagesActions.copyPageFailure), takeUntil(this.unsubscribe$)).subscribe(() => {
            this.form.enable({ emitEvent: false });
        });

        this.actions$.pipe(ofType(PagesActions.copyPageSuccess), takeUntil(this.unsubscribe$)).subscribe(() => {
            this.bsModalRef.hide();
        });
    }

    private initForm(): void {
        this.form = this.fb.group({
            name: this.fb.control(`${this.translate.instant('new_page')} ${this.formatDate}`, [Validators.required]),
            url: this.fb.control(null, [Validators.required]),
        });
    }

    public onConfirm(): void {
        if (this.form.invalid) {
            return;
        }

        this.form.disable({ emitEvent: false });
        this.pagesFacade.copyPage(Number(this.siteId), Number(this.page.id), {
            name: this.name.value,
            url: this.url.value,
        });
    }

    public onCancel(): void {
        this.bsModalRef.hide();
    }

    @HostListener('document:keydown.enter', ['$event'])
    public enterKeyEvent(event: KeyboardEvent): void {
        event.stopPropagation();

        if (this.disableConfirm) {
            return;
        }

        this.onConfirm();
    }

    public get disableConfirm(): boolean {
        return this.form.invalid || this.form.disabled || this.form.pending;
    }

    public get name(): CopyPageModalForm['name'] {
        return this.form.get('name') as CopyPageModalForm['name'];
    }

    public get url(): CopyPageModalForm['url'] {
        return this.form.get('url') as CopyPageModalForm['url'];
    }

    private get formatDate(): string {
        const d = new Date();

        return (
            [d.getMonth() + 1, d.getDate(), d.getFullYear()].join('/') +
            ' ' +
            [d.getHours(), d.getMinutes(), d.getSeconds()].join(':')
        );
    }

    public ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
